import { faXmark } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, IconButton, Stack, Stepper, Step, StepLabel } from '@vapor/material'
import { useTranslation } from 'react-i18next'
import FormLayoutDrawer from 'src/app/layout/form-layout/form-layout-drawer'
import { useContext, useEffect, useState } from 'react'
import StepOne from './steps/step-one'
import { PropertyTypeBuyNow } from './buy-now.type'
import StepTwo from './steps/step-two'
import BuyNowCrud from './buy-now.crud'
import { useProperty } from 'src/app/hooks/context/context.hook'
import { HICLoading } from 'hic-components'
import { hicContext } from 'src/app/hooks/context/context'
import { useAuth } from 'src/app/hooks/auth/auth.hook'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'

const APPARTMENT_CLASS_TYPE = ['casa vacanze', 'appartamento', 'villa']

export default function BuyNowDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const showError = useErrorSnackBar()
  const { t } = useTranslation()
  const property = useProperty()
  const { getBuyNowUrl } = useContext(hicContext)
  const { loginInfo } = useAuth()
  const { loading, propertyClassType, howManyResources, storeCatalog, subscriptionIdToUpgrade } = BuyNowCrud({ propertyId: property?.id ?? '', tenantId: property?.tenantId ?? '' })

  const [loadingBuy, setLoadingBuy] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [propertyType, setPropertyType] = useState<PropertyTypeBuyNow>('hotel')
  const [selectedPlanReference, setSelectedPlanReference] = useState<string>('NOT_SELECTED')

  useEffect(() => {
    if (propertyClassType) {
      setPropertyType(APPARTMENT_CLASS_TYPE.includes(propertyClassType.toLocaleLowerCase()) ? 'appartment' : 'hotel')
    }
  }, [propertyClassType])

  const pricePage = storeCatalog?.pricesPages?.find((pricePage) => {
    return propertyType === 'hotel' ? pricePage?.url?.includes('hotel') : pricePage?.url?.includes('case-vacanza')
  })
  const plans = pricePage?.cards?.[0 /* for now there is only one card */]?.plans ?? null
  const planIndex = plans?.length && howManyResources ? (howManyResources > plans?.length ? plans?.length - 1 : howManyResources - 1) : null
  const plan = planIndex !== null ? plans?.[planIndex] ?? null : null

  useEffect(() => {
    if (!open) setActiveStep(0)
  }, [open])

  useEffect(() => {
    setSelectedPlanReference(plan?.title ?? 'NOT_SELECTED')
  }, [plan])

  async function onFinalStep() {
    try {
      setLoadingBuy(true)
      if (loginInfo?.email) {
        const url = await getBuyNowUrl(loginInfo?.email, selectedPlanReference, subscriptionIdToUpgrade)
        if (url) {
          window.open(url, 'blank')
        }
      }
    } catch (error) {
      showError(error)
    } finally {
      setLoadingBuy(false)
    }
  }

  const steps: { label: string; step: number }[] = [
    {
      label: t('pms.buy_now.drawer.stepper_1'),
      step: 0,
    },
    {
      label: t('pms.buy_now.drawer.stepper_2'),
      step: 1,
    },
  ]

  return (
    <FormLayoutDrawer
      open={open}
      onClose={onClose}
      title={t('pms.buy_now.drawer.title')}
      tight
      titleOnLeft={true}
      rightNodes={[
        <IconButton onClick={onClose}>
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>,
      ]}
    >
      {open && (loading || loadingBuy) && <HICLoading />}
      <Stack justifyContent="space-between" height="100%">
        <Stack gap={2}>
          <Stepper activeStep={activeStep}>
            {steps.map((step) => {
              return (
                <Step key={step.step}>
                  <StepLabel>{step.label}</StepLabel>
                </Step>
              )
            })}
          </Stepper>
          {activeStep == 0 && <StepOne propertyType={propertyType} setPropertyType={setPropertyType} />}
          {activeStep == 1 && (
            <StepTwo
              plans={plans}
              planIndex={planIndex}
              selectedPlanReference={selectedPlanReference}
              setSelectedPlanReference={setSelectedPlanReference}
              propertyType={propertyType}
              onBuy={onFinalStep}
              disabled={loadingBuy}
            />
          )}
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Button
            variant="text"
            disabled={activeStep === 0 || loadingBuy}
            onClick={() => {
              setActiveStep((prev) => prev - 1)
            }}
          >
            {t('pms.buy_now.drawer.stepper.back')}
          </Button>
          <Button
            variant="text"
            disabled={loadingBuy}
            onClick={() => {
              if (activeStep < steps.length - 1) setActiveStep((prev) => prev + 1)
              else onFinalStep()
            }}
          >
            {t('pms.buy_now.drawer.stepper.next')}
          </Button>
        </Stack>
      </Stack>
    </FormLayoutDrawer>
  )
}
